import React, { useState, useEffect, useRef } from 'react';
import { 
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, 
  ResponsiveContainer 
} from 'recharts';
import { Activity, Wifi, WifiOff, Clock } from 'lucide-react';

// --- Binance WebSocket Hooks (Interval対応版) ---

function useBinanceKlines(symbol, interval, limit = 100) {
  const [data, setData] = useState([]);
  const [currentCandle, setCurrentCandle] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const wsRef = useRef(null);

  // interval変更時にデータをリセット
  useEffect(() => {
    setData([]);
    setCurrentCandle(null);
    setIsConnected(false);
    setIsLoading(true);
  }, [symbol, interval]);

  useEffect(() => {
    if (!symbol) return;

    // 1. 過去データの取得 (REST API)
    const fetchHistory = async () => {
      try {
        const url = `https://api.binance.com/api/v3/klines?symbol=${symbol}&interval=${interval}&limit=${limit}`;
        const response = await fetch(url);
        const rawData = await response.json();
        
        const formattedData = rawData.map(d => ({
          timestamp: d[0], // これを一意なキーとして使用
          open: parseFloat(d[1]),
          high: parseFloat(d[2]),
          low: parseFloat(d[3]),
          close: parseFloat(d[4]),
        }));
        
        setData(formattedData);
        setIsLoading(false);
      } catch (error) {
        console.error('Error fetching historical data:', error);
        setIsLoading(false);
      }
    };

    fetchHistory();

    // 2. リアルタイムデータの取得 (WebSocket)
    const wsUrl = `wss://stream.binance.com:9443/ws/${symbol.toLowerCase()}@kline_${interval}`;
    wsRef.current = new WebSocket(wsUrl);

    wsRef.current.onopen = () => setIsConnected(true);
    wsRef.current.onmessage = (event) => {
      const message = JSON.parse(event.data);
      const kline = message.k;
      
      const newCandle = {
        timestamp: kline.t,
        open: parseFloat(kline.o),
        high: parseFloat(kline.h),
        low: parseFloat(kline.l),
        close: parseFloat(kline.c),
        isClosed: kline.x
      };

      setCurrentCandle(newCandle);

      // 足が確定したら履歴に追加
      if (kline.x) {
        setData(prev => {
            // 重複を防ぎつつ追加（timestampで比較）
            const filtered = prev.filter(p => p.timestamp !== newCandle.timestamp);
            return [...filtered.slice(-(limit - 1)), newCandle];
        });
      }
    };

    // クリーンアップ
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [symbol, interval, limit]);

  return { data, currentCandle, isConnected, isLoading };
}

function useBinanceTicker(symbol) {
  const [ticker, setTicker] = useState(null);
  const wsRef = useRef(null);

  useEffect(() => {
    setTicker(null);
  }, [symbol]);

  useEffect(() => {
    if (!symbol) return;
    const wsUrl = `wss://stream.binance.com:9443/ws/${symbol.toLowerCase()}@ticker`;
    wsRef.current = new WebSocket(wsUrl);

    wsRef.current.onmessage = (event) => {
      const data = JSON.parse(event.data);
      setTicker({
        price: parseFloat(data.c),
        priceChangePercent: parseFloat(data.P),
      });
    };

    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [symbol]);

  return ticker;
}

// --- Main Component ---

export default function CryptoChartBlock({ selectedAssetId, assets, isDarkMode, isMobile, commonStyles }) {
  // 時間足の状態管理
  const [interval, setInterval] = useState('1h');

  // 時間足の選択肢
  const intervals = [
    { label: '1分', value: '1m' },
    { label: '5分', value: '5m' },
    { label: '15分', value: '15m' },
    { label: '1H', value: '1h' },
    { label: '4H', value: '4h' },
    { label: '日足', value: '1d' },
    { label: '週足', value: '1w' },
    { label: '月足', value: '1M' },
  ];

  const getBinanceSymbol = (id) => {
    // 1. 固定マッピング
    const staticMap = {
      'btc': 'BTCUSDT',
      'eth': 'ETHUSDT',
      'bnb': 'BNBUSDT',
      'sol': 'SOLUSDT',
      'xrp': 'XRPUSDT',
      'ada': 'ADAUSDT',
    };
    if (staticMap[id]) return staticMap[id];

    // 2. 動的マッピング
    const asset = assets?.find(a => a.id === id);
    if (asset && asset.category === '仮想通貨') {
      const match = asset.name.match(/\(([^)]+)\)/);
      if (match && match[1]) {
        return `${match[1].toUpperCase()}USDT`;
      }
      if (!id.startsWith('custom_')) {
         return `${id.toUpperCase()}USDT`;
      }
    }
    return null;
  };

  const symbol = getBinanceSymbol(selectedAssetId);
  
  // データ取得 (100件取得)
  const { data, currentCandle, isConnected, isLoading } = useBinanceKlines(symbol, interval, 100);
  const ticker = useBinanceTicker(symbol);

  if (!symbol) return null;

  // 現在進行中の足を含めてチャートデータを作成
  const chartData = currentCandle && !currentCandle.isClosed 
    ? [...data, currentCandle]
    : data;

  const currentPrice = ticker ? ticker.price : (currentCandle ? currentCandle.close : 0);
  const priceColor = ticker && ticker.priceChangePercent >= 0 ? '#10b981' : '#ef4444';
  
  // スタイル設定
  const strokeColor = "#6366f1"; 
  const gridColor = isDarkMode ? "rgba(255,255,255,0.1)" : "#e0e0e0";
  const axisTextColor = isDarkMode ? "#9ca3af" : "#64748b";
  const tooltipBg = isDarkMode ? "#1f2937" : "#ffffff";
  const tooltipBorder = isDarkMode ? "#374151" : "#e5e7eb";
  const tooltipText = isDarkMode ? "#f3f4f6" : "#1f2937";

  // 日付フォーマッター（軸とツールチップで共有）
  const dateFormatter = (unixTime) => {
    const date = new Date(unixTime);
    // 月足・週足・日足の場合は日付まで、それ以外は時刻を含める
    if (interval.includes('M') || interval.includes('w') || interval === '1d') {
        return date.toLocaleDateString('ja-JP', { month: 'numeric', day: 'numeric' });
    }
    return date.toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit' });
  };
  
  // ツールチップ用の詳細フォーマッター
  const tooltipDateFormatter = (unixTime) => {
    return new Date(unixTime).toLocaleString('ja-JP', {
        month: 'numeric',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    });
  };

  return (
    <div style={commonStyles.dynamicBlockCard(isDarkMode, isMobile)}>
      {/* Header Area */}
      <div style={{
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        justifyContent: 'space-between',
        alignItems: isMobile ? 'flex-start' : 'center',
        gap: '15px',
        marginBottom: '15px',
        borderBottom: isDarkMode ? '1px solid rgba(255,255,255,0.05)' : '1px solid #f0f0f0',
        paddingBottom: '10px'
      }}>
        {/* Left: Title & Controls */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', width: isMobile ? '100%' : 'auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', ...commonStyles.dynamicBlockTitle(isDarkMode) }}>
            <Activity size={18} />
            <span>マーケットチャート ({symbol})</span>
          </div>

          {/* 時間足切り替えボタン */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
            {intervals.map((int) => (
              <button
                key={int.value}
                onClick={() => setInterval(int.value)}
                style={{
                  padding: '4px 8px',
                  fontSize: '11px',
                  borderRadius: '4px',
                  border: interval === int.value 
                    ? `1px solid ${strokeColor}` 
                    : isDarkMode ? '1px solid #333' : '1px solid #ddd',
                  background: interval === int.value 
                    ? strokeColor 
                    : isDarkMode ? 'rgba(255,255,255,0.05)' : '#fff',
                  color: interval === int.value ? '#fff' : axisTextColor,
                  cursor: 'pointer',
                  fontWeight: interval === int.value ? 'bold' : 'normal',
                  transition: 'all 0.2s'
                }}
              >
                {int.label}
              </button>
            ))}
          </div>
        </div>
        
        {/* Right: Price & Status */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px', alignSelf: isMobile ? 'flex-end' : 'auto' }}>
          {ticker && (
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
              <span style={{ fontSize: '20px', fontWeight: 'bold', color: isDarkMode ? '#fff' : '#1e293b' }}>
                ${currentPrice.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
              <span style={{ fontSize: '13px', color: priceColor, fontWeight: 'bold' }}>
                {ticker.priceChangePercent > 0 ? '+' : ''}{ticker.priceChangePercent}%
              </span>
            </div>
          )}
          {isConnected ? 
            <Wifi size={16} color="#10b981" title="Connected" /> : 
            <WifiOff size={16} color="#ef4444" title="Disconnected" />
          }
        </div>
      </div>

      {/* Chart Area */}
      <div style={{ height: '350px', width: '100%', position: 'relative' }}>
        {isLoading && (
            <div style={{
                position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: isDarkMode ? 'rgba(0,0,0,0.5)' : 'rgba(255,255,255,0.5)', zIndex: 10
            }}>
                <Clock size={24} className="animate-spin" color={axisTextColor} />
            </div>
        )}

        {chartData.length > 0 ? (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={gridColor} vertical={false} />
              
              {/* 修正ポイント: dataKeyをtimestampに変更し、type="number"を指定 */}
              <XAxis 
                dataKey="timestamp" 
                type="number"
                domain={['dataMin', 'dataMax']}
                scale="time"
                stroke={axisTextColor}
                fontSize={10}
                tickLine={false}
                axisLine={false}
                minTickGap={40}
                tickFormatter={dateFormatter}
              />
              
              <YAxis 
                domain={['auto', 'auto']}
                stroke={axisTextColor}
                fontSize={10}
                tickLine={false}
                axisLine={false}
                tickFormatter={(val) => `$${val.toLocaleString()}`}
                width={60}
              />
              
              {/* 修正ポイント: labelFormatterを追加 */}
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: tooltipBg, 
                  border: `1px solid ${tooltipBorder}`,
                  borderRadius: '8px',
                  color: tooltipText
                }}
                labelStyle={{ color: axisTextColor, marginBottom: '4px' }}
                itemStyle={{ color: strokeColor }}
                formatter={(value) => [`$${value.toLocaleString()}`, 'Price']}
                labelFormatter={tooltipDateFormatter}
              />
              
              <Line 
                type="monotone" 
                dataKey="close" 
                stroke={strokeColor} 
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4, fill: strokeColor }}
                isAnimationActive={false}
              />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div style={{ 
            height: '100%', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            color: axisTextColor 
          }}>
            {!isLoading && "データ待機中..."}
          </div>
        )}
      </div>
    </div>
  );
}